import findFacetByParamKey from "./findFacet";
import composeFieldName from "./composeFieldName";
import isFacetSearchParam from "./isFacetSearchParam";
import isRangeSearchParam from "./isRangeSearchParam";
import { nonFacetSearchParams } from "./nonFacetSearchParams";
import { showAllQuery } from "../../context/DataContext";
import Facet from "../../types/funnelback/Facets";
import { SearchFormInitialValues } from "./SearchForm";
import {
    getFacetParamName,
    getFacetParamValue,
} from "../../utils/facetParams";

/*
 * - Deserialise URL search params to Formik values
 * - Transform Funnelback URL search param keys into easily parsed field names
 * - Transform Funnelback URL search param values into format compatible with
 *   Formik values
 */
const composeFormikValues = (
    searchParams: URLSearchParams,
    facets: Facet[]
): SearchFormInitialValues => {
    const values: SearchFormInitialValues = {
        query: "",
    };
    // Set initial values from non-facet URL search params
    const nonFacetKeys: string[] = nonFacetSearchParams.map(
        (paramConfig) => paramConfig.paramKey
    );
    nonFacetKeys.forEach((key) => {
        const paramValue = searchParams.get(key);
        if (paramValue) {
            /*
             * Special case: the internal Funnelback query value
             * to show all results should display as an empty
             * string to the front-end user.
             */
            if (key === "query" && paramValue === showAllQuery) {
                values[key] = "";
            } else {
                values[key] = paramValue;
            }
        }
    });

    const processedFacetParamKeys = new Set<string>();

    const isFacetOrRangeParam = (key: string): boolean =>
        isFacetSearchParam(key) || isRangeSearchParam(key);

    searchParams.forEach((_value, key) => {
        if (!isFacetOrRangeParam(key) || processedFacetParamKeys.has(key)) {
            return;
        }
        processedFacetParamKeys.add(key);

        const matchingFacet = findFacetByParamKey(key, facets);
        if (!matchingFacet) {
            /*
             * No matching facet found:
             * Continue to next URL search param.
             */
            return;
        }
        const { selectedValues, selectionType } = matchingFacet;
        const facetParamName = getFacetParamName(matchingFacet);
        if (!facetParamName) {
            return;
        }

        const fieldKey = composeFieldName(facetParamName);
        const hasMultipleValues = selectionType === "MULTIPLE";
        const urlValues = searchParams
            .getAll(key)
            .map((paramValue) => paramValue.trim())
            .filter(Boolean);

        if (selectedValues && selectedValues.length) {
            /*
             * Store facets that allow multiple values as an array
             * and facets that only allow single values as a string.
             */
            values[fieldKey] = hasMultipleValues
                ? selectedValues.map((facetValue) =>
                      getFacetParamValue(matchingFacet, facetValue)
                  )
                : getFacetParamValue(matchingFacet, selectedValues[0]);
        } else if (urlValues.length) {
            /*
             * Fall back to URL params when the API response has not yet
             * reflected the selection (e.g. immediately after Apply).
             */
            values[fieldKey] = hasMultipleValues ? urlValues : urlValues[0];
        } else {
            values[fieldKey] = hasMultipleValues ? [] : "";
        }
    });
    return values;
};

export default composeFormikValues;
